import { Chain } from "./chain";
import { Pt, ptEquals } from "./pt";
import { intersections, IntersectionPoint } from "./intersections";
import { Segment, LineSegment } from "./segment";

interface SplitResult {
  split1: Chain[];
  split2: Chain[];
  arcs1: Chain[];
  arcs2: Chain[];
}

function split(chains1: Chain[], chains2: Chain[]): SplitResult {
  const ips = findIntersections(chains1, chains2);
  const tMap = intersectionMap(ips);
  const split1 = chains1.map((c) => splitChain(c, tMap));
  const split2 = chains2.map((c) => splitChain(c, tMap));
  const part1 = partition(split1, ips);
  const part2 = partition(split2, ips);
  const pool = new PointPool();
  const arcs1 = part1.map((a) => adjust(a, pool));
  const arcs2 = part2.map((a) => adjust(a, pool));

  return { split1, split2, arcs1, arcs2 };
}

function partition(closedChains: Chain[], ips: IntersectionPoint[]): Chain[] {
  let result = [];
  closedChains.forEach((c) => {
    let offset = c.segments.findIndex((segment) => {
      let segStart = ips.find((ip) => ptEquals(ip.pt, segment.start));
      return segStart == undefined;
    });
    if (offset == -1) result.push(c);
    else {
      var acc = [];
      for (let i = offset; i < c.segments.length; i++) {
        let s = c.segments[i];
        if (!ptEquals(s.start, s.end)) {
          if (ips.find((ip) => ptEquals(ip.pt, s.start))) {
            if (acc.length > 0) {
              result.push(new Chain(acc));
              acc = [s];
            }
          } else {
            acc.push(s);
          }
        }
      }

      if (acc.length > 0) {
        result.push(new Chain(acc));
        acc = [];
      }

      for (let i = 0; i < offset; i++) acc.push(c.segments[i]);

      if (acc.length > 0) result.push(new Chain(acc));
    }
  });
  return result;
}

function findIntersections(aChains: Chain[], bChains: Chain[]) {
  return aChains.flatMap((c1) => {
    return c1.segments.flatMap((s1) => {
      return bChains.flatMap((c2) => {
        return c2.segments.flatMap((s2) => intersections(s1, s2));
      });
    });
  });
}

function intersectionMap(
  ips: IntersectionPoint[]
): Map<Segment, IntersectionPoint[]> {
  let tMap = new Map();
  ips.forEach((ip) => {
    let ts = tMap.get(ip.segment);
    if (ts == undefined) tMap.set(ip.segment, [ip]);
    else ts.push(ip);
  });
  return tMap;
}

function splitChain(
  chain: Chain,
  tMap: Map<Segment, IntersectionPoint[]>
): Chain {
  return new Chain(
    chain.segments.flatMap((s) => {
      let ips = tMap.get(s);
      if (ips == undefined) return [s];
      else return splitSegment(s, ips);
    })
  );
}

function splitSegment(segment: Segment, ips: IntersectionPoint[]): Segment[] {
  let ips2 = ips.filter((ip) => ip.pos > 0 && ip.pos < 1);
  if (ips2.length == 0) return [segment];

  ips2.sort((x, y) => x.pos - y.pos);

  let result = [];
  const pair = segment.split(ips2[0].pos, ips2[0].pt);
  result[0] = pair[0];
  let remaining = pair[1];
  for (let i = 1; i < ips2.length; i++) {
    const pair = remaining.split(ips2[i].pos, ips2[i].pt);
    result[i] = pair[0];
    remaining = pair[1];
  }
  result[ips2.length] = remaining;

  return result;
}

class PointPool {
  points: Pt[];
  constructor() {
    this.points = [];
  }

  register(pt: Pt): Pt {
    let found = this.points.find((p) => ptEquals(p, pt));
    if (found) return found;
    else {
      this.points.push(pt);
      return pt;
    }
  }
}

function adjust(chain: Chain, pool: PointPool): Chain {
  const newStart = pool.register(chain.start);
  const newEnd = pool.register(chain.end);
  return chain.endpoints(newStart, newEnd);
}

export { split };
